import React from "react";
import { Box } from "@chakra-ui/react";

/**
 * SectionDivider — Séparateur architectural raffiné
 *
 * @param fromColor   Couleur de la section du dessus
 * @param toColor     Couleur de la section du dessous
 * @param flip        Inverse le sens de la diagonale
 * @param height      Hauteur du séparateur en px (défaut: 120)
 * @param variant     Style de coupe : "diagonal" | "sharp" | "step"
 */
interface SectionDividerProps {
    fromColor: string;
    toColor: string;
    flip?: boolean;
    height?: number;
    variant?: "diagonal" | "sharp" | "step";
}

const SectionDivider: React.FC<SectionDividerProps> = ({
    fromColor,
    toColor,
    flip = false,
    height = 120,
    variant = "diagonal",
}) => {

    // ── Variante "diagonal" : coupe biseautée avec ligne dorée fine ──────────
    const DiagonalShape = () => (
        <svg
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "block",
                transform: flip ? "scaleX(-1)" : "none",
            }}
        >
            <defs>
                {/* Dégradé pour la ligne de séparation */}
                <linearGradient id={`lineGrad-${flip}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%"   stopColor="transparent" />
                    <stop offset="20%"  stopColor="#C9A84C" stopOpacity="0.6" />
                    <stop offset="50%"  stopColor="#C9A84C" stopOpacity="0.9" />
                    <stop offset="80%"  stopColor="#8B9E6E" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="transparent" />
                </linearGradient>
                {/* Lueur sous la ligne */}
                <filter id="glow">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            {/* Zone toColor en bas */}
            <polygon points="0,120 1440,0 1440,120" fill={toColor} />

            {/* Ligne de coupe lumineuse */}
            <line
                x1="0" y1="120"
                x2="1440" y2="0"
                stroke={`url(#lineGrad-${flip})`}
                strokeWidth="1.5"
                filter="url(#glow)"
            />
        </svg>
    );

    // ── Variante "sharp" : double coupe en V très aplati ────────────────────
    const SharpShape = () => (
        <svg
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "block",
                transform: flip ? "scaleX(-1)" : "none",
            }}
        >
            <defs>
                <linearGradient id={`sharpGrad-${flip}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%"   stopColor="transparent" />
                    <stop offset="30%"  stopColor="#C9A84C" stopOpacity="0.5" />
                    <stop offset="50%"  stopColor="#E8D4A0" stopOpacity="0.8" />
                    <stop offset="70%"  stopColor="#8B9E6E" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="transparent" />
                </linearGradient>
                <filter id="sharpGlow">
                    <feGaussianBlur stdDeviation="1.5" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            {/* Zone toColor : pointe centrale pointant vers le haut */}
            <polygon
                points="0,120 720,18 1440,120"
                fill={toColor}
            />

            {/* Arête lumineuse gauche */}
            <line
                x1="0" y1="120"
                x2="720" y2="18"
                stroke={`url(#sharpGrad-${flip})`}
                strokeWidth="1.2"
                filter="url(#sharpGlow)"
            />
            {/* Arête lumineuse droite */}
            <line
                x1="720" y1="18"
                x2="1440" y2="120"
                stroke={`url(#sharpGrad-${flip})`}
                strokeWidth="1.2"
                filter="url(#sharpGlow)"
            />
        </svg>
    );

    // ── Variante "step" : marche architecturale asymétrique ─────────────────
    const StepShape = () => (
        <svg
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "block",
                transform: flip ? "scaleX(-1)" : "none",
            }}
        >
            <defs>
                <linearGradient id={`stepGrad-${flip}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%"   stopColor="transparent" />
                    <stop offset="15%"  stopColor="#C9A84C" stopOpacity="0.7" />
                    <stop offset="45%"  stopColor="#C9A84C" stopOpacity="0.4" />
                    <stop offset="55%"  stopColor="#8B9E6E" stopOpacity="0.4" />
                    <stop offset="85%"  stopColor="#8B9E6E" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="transparent" />
                </linearGradient>
                <filter id="stepGlow">
                    <feGaussianBlur stdDeviation="1.8" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            {/*
              Marche : segment horizontal en haut à gauche,
              puis descente verticale au tiers, puis segment horizontal jusqu'à droite
            */}
            <polygon
                points="0,40 480,40 480,120 1440,120 1440,80 960,80 960,0 0,0"
                fill={fromColor}
            />
            <polygon
                points="0,40 480,40 480,120 0,120"
                fill={toColor}
            />
            <polygon
                points="480,40 960,0 1440,0 1440,80 960,80 960,0"
                fill={toColor}
                opacity="0.6"
            />
            <polygon
                points="480,40 960,80 1440,80 1440,120 480,120"
                fill={toColor}
            />

            {/* Lignes lumineuses sur les arêtes */}
            <polyline
                points="0,40 480,40 960,0 1440,0"
                stroke={`url(#stepGrad-${flip})`}
                strokeWidth="1.2"
                fill="none"
                filter="url(#stepGlow)"
            />
            <polyline
                points="480,40 480,120"
                stroke={`url(#stepGrad-${flip})`}
                strokeWidth="1.2"
                fill="none"
                filter="url(#stepGlow)"
            />
        </svg>
    );

    const shapes = {
        diagonal: <DiagonalShape />,
        sharp:    <SharpShape />,
        step:     <StepShape />,
    };

    return (
        <Box
            position="relative"
            w="100%"
            h={`${height}px`}
            bg={fromColor}
            flexShrink={0}
            overflow="hidden"
        >
            {shapes[variant]}
        </Box>
    );
};

export default SectionDivider;